import { useEffect } from 'react';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { isProdEnvironment } from '@/utils/environment';
import { injectPylonWidget, isPylonEligibleUser } from '@/utils/pylon';

export function PylonChatProvider() {
  const { user } = useCurrentUser();
  const appId = import.meta.env.VITE_PYLON_APP_ID;

  useEffect(() => {
    // Non-production environments never load the widget, so there is nothing to
    // tear down and support does not receive conversations from test accounts.
    if (!appId || !isProdEnvironment()) return;

    if (!user || !isPylonEligibleUser(user)) {
      // Logout and account switching happen without a page reload, and Pylon
      // exposes no logout call, so the only supported reset is dropping the
      // identity settings and hiding the already-loaded widget. Skip when the
      // stub was never installed so a later first boot does not replay hide.
      window.pylon = undefined;
      if (window.Pylon) {
        window.Pylon('hide');
        window.Pylon('hideChatBubble');
      }
      return;
    }

    // Identity must be in place before the widget boots, otherwise Pylon
    // rejects the session when identity verification is enabled.
    window.pylon = {
      chat_settings: {
        app_id: appId,
        email: user.email,
        name: user.name,
        avatar_url: user.image ?? undefined,
        email_hash: user.pylon_email_hash,
      },
    };

    injectPylonWidget(appId);

    window.Pylon?.('showChatBubble');
    window.Pylon?.('setNewIssueCustomFields', {
      user_id: user.id,
      user_role: user.role,
      recruiter_type: user.recruiter_type,
    });
  }, [user, appId]);

  return null;
}
